/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useFeatureFlag } from '@/features/globalConfig';
import { type QueryClient } from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';
import { getThirdPartyAxios } from '@/lib/axios/thirdPartyClient';
import { type GetCityStoresResponse, type GetCityStoresQueryParams } from '../interface/yext-types';
import { useLocationsParams } from '../hooks/useLocationsParams';
import { type Locale } from '@/types/i18n';
import { useLocale } from '@/hooks/useLocale';
import { locationsPrefix } from '../constants/locationsPrefix';
import { getYextCountryCodes } from '../utils/getYextCountryCodes';
import { createQuery } from '@/utils/createReactQuery';

export type GetStateStoreOptions = {
  yextURL: string;
  yextAPIKey: string;
  yextVersion: string;
  locale: Locale;
  state: string;
};

const getStateStore = async (options: GetStateStoreOptions, _axiosInstance?: AxiosInstance) => {
  const { yextURL, yextAPIKey, yextVersion, locale, state } = options;

  const missingYextParams: string[] = [];

  if (!yextURL) {
    missingYextParams.push('YEXT_URL');
  }
  if (!yextAPIKey) {
    missingYextParams.push('YEXT_API_KEY');
  }
  if (!yextVersion) {
    missingYextParams.push('YEXT_VERSION');
  }

  if (missingYextParams.length > 0) {
    throw new Error(`Missing Yext parameters in globalConfig: ${missingYextParams.join(', ')}`);
  }

  if (!state) {
    throw new Error('State is required to fetch state stores');
  }

  const locationsHome = locationsPrefix[locale];

  const params: GetCityStoresQueryParams = {
    entityType: 'location',
    limit: 1,
    fields: ['name', 'address'].join(','),
    filter: JSON.stringify({
      countryCode: { $in: getYextCountryCodes(locale) },
      'websiteUrl.url': {
        $contains: `/${locationsHome}/${encodeURIComponent(state)}/`,
      },
      closed: { $eq: false },
    }),
    v: yextVersion,
  };

  const client = getThirdPartyAxios();

  const response = await client.get<GetCityStoresResponse>(yextURL + 'me/entities', {
    params,
    headers: {
      'api-key': yextAPIKey,
    },
  });

  return response.data;
};

const { useData, prefetch } = createQuery('stateStores', getStateStore);

/**
 * TEMPORARY: This hook queries Yext to fetch a single store for a given state.
 * This is a workaround until the backend provides a dedicated endpoint for state-level store information.
 * Once the BE implements this endpoint, this file should be removed
 * and replaced with a direct API call to the new backend endpoint.
 */
export const useStateStore = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const yextURL = useFeatureFlag('YEXT_URL') ?? '';
  const yextAPIKey = useFeatureFlag('YEXT_API_KEY') ?? '';
  const yextVersion = useFeatureFlag('YEXT_VERSION') ?? '';

  const locale = useLocale();
  const { state = '' } = useLocationsParams();

  return useData({
    yextURL,
    yextAPIKey,
    yextVersion,
    state,
    locale,
    enabled: enabled && Boolean(yextURL && yextAPIKey && yextVersion && state),
  });
};

export const prefetchStateStore = async (
  queryClient: QueryClient,
  axios: AxiosInstance,
  options: GetStateStoreOptions,
) => {
  return prefetch(queryClient, options, axios);
};
